import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import SellerAvatar from "@/components/SellerAvatar";
import { api } from "@/lib/api";
import { BroadcastIcon, FireIcon, LightningIcon, UsersThreeIcon } from "@phosphor-icons/react";

/**
 * MomentumCarousel — premium 3D coverflow of live shows for the
 * Bidorah homepage. Beats Whatnot's "Shows with Momentum" by stacking
 * three motion concepts:
 *
 *   #1 Vault Coverflow  — true 3D rotation (preserve-3d), mirrored
 *                         reflection, gold metallic edge, breathing
 *                         scale on the center card.
 *   #2 Spotlight Stage  — a searchlight gradient sweeps over the
 *                         center card as it lands, side cards sit in
 *                         shadow (dimmed + desaturated).
 *   #5 Live Aura        — when the show is genuinely hot (server-side
 *                         `heat_level: warm/hot/frenzy` or peak_viewers
 *                         spike), the center card grows an animated
 *                         pulsing aura ring whose color escalates
 *                         amber → red → magenta with the temperature.
 *
 * Power-ups baked in:
 *   • Spring-physics drag (framer-motion) — overshoots, settles
 *   • Auto-advance every 5.5s, pauses while the user is touching
 *   • Tactile haptic ping on snap via `navigator.vibrate` (no-ops on
 *     desktop, fires on Android/Chrome mobile, gracefully ignored on iOS)
 *   • Count-up viewer number when a card snaps into focus
 *   • "Hot" badge re-uses the same heat data as the Hot Lot tracker
 *
 * Mobile-first; responsive up to wide screens. Honors prefers-reduced-motion.
 */

// Visual lookup for each heat tier. Aura is layered: the inner glow
// uses `--aura-inner`, outer halo `--aura-outer`. Higher tiers pulse
// faster (see CSS keyframes `momentum-aura-*`).
const HEAT_VISUALS = {
  cold:    { hasAura: false, label: null,           pulse: 0,    accent: "#94a3b8" },
  warm:    { hasAura: true,  label: "Warming up",   pulse: 1,    accent: "#fbbf24" },
  hot:     { hasAura: true,  label: "Hot",          pulse: 2,    accent: "#f97316" },
  frenzy:  { hasAura: true,  label: "FRENZY",       pulse: 3,    accent: "#ef4444" },
};

// Card category → backdrop gradient. Used when the show has no
// thumbnail yet so cards never look "empty".
const CATEGORY_GRADIENT = {
  Sneakers:                "linear-gradient(135deg,#0f172a 0%,#1e3a8a 100%)",
  "Fragrances & Perfume":  "linear-gradient(135deg,#831843 0%,#581c87 100%)",
  "Luxury Bags & Accessories": "linear-gradient(135deg,#3b0764 0%,#1e1b4b 100%)",
  "Storage Unit Finds":    "linear-gradient(135deg,#422006 0%,#7c2d12 100%)",
  "Trading Card Games":    "linear-gradient(135deg,#172554 0%,#581c87 100%)",
  Electronics:             "linear-gradient(135deg,#020617 0%,#0c4a6e 100%)",
  Funko:                   "linear-gradient(135deg,#7c2d12 0%,#9a3412 100%)",
  "Rocks & Crystals":      "linear-gradient(135deg,#064e3b 0%,#0c4a6e 100%)",
  "Sports Cards":          "linear-gradient(135deg,#0c4a6e 0%,#1e3a8a 100%)",
  Plants:                  "linear-gradient(135deg,#14532d 0%,#365314 100%)",
  "Women's Apparel":       "linear-gradient(135deg,#500724 0%,#831843 100%)",
  Jewelry:                 "linear-gradient(135deg,#422006 0%,#451a03 100%)",
  Digital:                 "linear-gradient(135deg,#1e1b4b 0%,#312e81 100%)",
};

// Small custom hook — smoothly count from prev → next when value changes.
function useCountUp(value, durationMs = 700) {
  const [display, setDisplay] = useState(value || 0);
  const fromRef = useRef(value || 0);
  useEffect(() => {
    const start = performance.now();
    const from = fromRef.current;
    const to = Number(value || 0);
    let raf;
    const tick = (t) => {
      const p = Math.min(1, (t - start) / durationMs);
      // ease-out
      const eased = 1 - Math.pow(1 - p, 3);
      setDisplay(Math.round(from + (to - from) * eased));
      if (p < 1) raf = requestAnimationFrame(tick);
      else fromRef.current = to;
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [value, durationMs]);
  return display;
}

export default function MomentumCarousel() {
  const [shows, setShows] = useState([]);
  const [active, setActive] = useState(0);
  const [paused, setPaused] = useState(false);

  // Load live shows on mount. Auto-prefer featured streams; fall back
  // to the generic live list so the carousel is never empty when a
  // moderator hasn't curated featured shows yet.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [feat, live] = await Promise.all([
          api.get("/featured/streams", { params: { limit: 8 } }).catch(() => ({ data: { items: [] } })),
          api.get("/streams", { params: { status: "live", limit: 12 } }),
        ]);
        if (cancelled) return;
        const seen = new Set();
        const merged = [...(feat.data?.items || []), ...(live.data?.items || [])].filter((s) => {
          if (seen.has(s.id)) return false;
          seen.add(s.id);
          return true;
        }).slice(0, 8);
        setShows(merged);
      } catch (e) {
        // intentionally swallow — empty array = component self-hides
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // Auto-advance loop. Pauses when the user is touching the carousel
  // OR when the document is hidden (saves CPU on backgrounded tabs).
  useEffect(() => {
    if (paused || shows.length <= 1) return;
    if (document.visibilityState === "hidden") return;
    const t = setInterval(() => {
      setActive((i) => (i + 1) % shows.length);
    }, 5500);
    return () => clearInterval(t);
  }, [paused, shows.length, active]);

  // Snap haptic on Android. iOS Safari ignores navigator.vibrate; that's fine.
  useEffect(() => {
    if (shows.length && typeof navigator !== "undefined" && navigator.vibrate) {
      try { navigator.vibrate(12); } catch { /* no-op */ }
    }
  }, [active, shows.length]);

  const goTo = (i) => {
    if (!shows.length) return;
    const next = ((i % shows.length) + shows.length) % shows.length;
    setActive(next);
  };

  if (!shows.length) return null;

  return (
    <section
      className="relative bg-gradient-to-b from-zinc-950 via-[#0a0d1a] to-zinc-950 text-white overflow-hidden"
      data-testid="momentum-carousel"
    >
      {/* Star-field + radial glow background, matches the Beacon system */}
      <div className="absolute inset-0 beacon-stars opacity-50 pointer-events-none" />
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_30%,rgba(252,211,77,0.15),transparent_55%)] pointer-events-none" />

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-16">
        {/* Header */}
        <div className="flex flex-wrap items-end justify-between gap-3 mb-3 sm:mb-5 px-2">
          <div>
            <p className="overline text-amber-300 mb-2 flex items-center gap-2">
              <BroadcastIcon size={12} weight="fill" />
              Shows in the Beacon
            </p>
            <h2 className="font-display font-black text-3xl sm:text-5xl tracking-tighter leading-[0.95]">
              Sales are heating up
              <br className="hidden sm:block" />
              <span className="text-amber-300"> right now.</span>
            </h2>
          </div>
          <Link
            to="/live"
            className="inline-flex items-center gap-1 rounded-full border border-amber-300/40 hover:border-amber-200 px-4 py-2 text-xs font-bold uppercase tracking-widest text-amber-200 hover:text-amber-100 transition"
            data-testid="momentum-show-all"
          >
            Show all →
          </Link>
        </div>

        {/* The stage */}
        <Stage
          shows={shows}
          active={active}
          onSnap={(i) => goTo(i)}
          onInteractStart={() => setPaused(true)}
          onInteractEnd={() => setPaused(false)}
        />

        {/* Dot indicator */}
        <div className="mt-6 sm:mt-8 flex items-center justify-center gap-2" data-testid="momentum-dots">
          {shows.map((s, i) => (
            <button
              key={s.id}
              type="button"
              onClick={() => goTo(i)}
              aria-label={`Show ${i + 1}`}
              data-testid={`momentum-dot-${i}`}
              className={`h-1.5 rounded-full transition-all ${
                i === active
                  ? "w-8 bg-amber-300"
                  : "w-1.5 bg-white/30 hover:bg-white/50"
              }`}
            />
          ))}
        </div>
      </div>
    </section>
  );
}

/* ============================================================
   <Stage /> — the 3D vault. Each show is a CoverCard, positioned
   in space by `offset = idx - active`. We render the immediate
   neighbours (-2, -1, 0, +1, +2) so we have visual depth without
   wasting GPU on offscreen cards.
   ============================================================ */
function Stage({ shows, active, onSnap, onInteractStart, onInteractEnd }) {
  // Compute relative position for each card.
  const visible = useMemo(() => {
    const arr = [];
    for (let off = -2; off <= 2; off++) {
      const i = (active + off + shows.length) % shows.length;
      arr.push({ show: shows[i], offset: off, idx: i });
    }
    return arr;
  }, [shows, active]);

  return (
    <div
      className="relative w-full mx-auto"
      style={{ perspective: "1400px" }}
      onPointerDown={onInteractStart}
      onPointerUp={onInteractEnd}
      onPointerLeave={onInteractEnd}
      data-testid="momentum-stage"
    >
      <motion.div
        className="relative mx-auto h-[420px] sm:h-[480px] flex items-center justify-center"
        style={{ transformStyle: "preserve-3d" }}
        drag="x"
        dragConstraints={{ left: 0, right: 0 }}
        dragElastic={0.18}
        onDragEnd={(_, info) => {
          // Threshold-based commit: anything past 60px (or fast flick)
          // commits to the next/previous card. Spring physics handles the
          // rest of the visual feel.
          const TH = 60;
          if (info.offset.x < -TH || info.velocity.x < -400) onSnap(active + 1);
          else if (info.offset.x > TH || info.velocity.x > 400) onSnap(active - 1);
        }}
      >
        <AnimatePresence initial={false}>
          {visible.map(({ show, offset, idx }) => (
            <CoverCard
              key={show.id}
              show={show}
              offset={offset}
              focused={offset === 0}
              onClick={() => (offset === 0 ? null : onSnap(idx))}
            />
          ))}
        </AnimatePresence>
      </motion.div>
    </div>
  );
}

/* ============================================================
   <CoverCard /> — the centerpiece. 3D rotated, mirrored, optionally
   auraed, with a spotlight overlay when focused.
   ============================================================ */
function CoverCard({ show, offset, focused, onClick }) {
  const viewers = useCountUp(focused ? (show.viewer_count || 0) : 0);
  const heat = HEAT_VISUALS[show.heat_level] || HEAT_VISUALS.cold;

  // Math of the coverflow:
  //   offset =  0 → center  (0° rotateY, scale 1, z 0)
  //   offset = ±1 → side    (∓35° rotateY, scale 0.82, z back)
  //   offset = ±2 → fringe  (∓55° rotateY, scale 0.62, z further)
  const x = offset * 175;
  const rotY = -offset * 35;
  const z = -Math.abs(offset) * 120;
  const scale = offset === 0 ? 1 : Math.abs(offset) === 1 ? 0.82 : 0.62;
  const opacity = Math.abs(offset) > 2 ? 0 : offset === 0 ? 1 : Math.abs(offset) === 1 ? 0.7 : 0.4;
  const zIndex = 10 - Math.abs(offset);

  const bg = show.cover_image
    ? `url(${show.cover_image}) center/cover`
    : CATEGORY_GRADIENT[show.category] || "linear-gradient(135deg,#1e293b,#0f172a)";

  return (
    <motion.div
      data-testid={`momentum-card-${offset}`}
      className="absolute"
      style={{
        transformStyle: "preserve-3d",
        zIndex,
        pointerEvents: focused ? "auto" : "auto",
      }}
      initial={{ opacity: 0, scale: scale * 0.9 }}
      animate={{
        x,
        rotateY: rotY,
        z,
        scale,
        opacity,
      }}
      exit={{ opacity: 0, scale: scale * 0.85 }}
      transition={{
        type: "spring",
        stiffness: 220,
        damping: 26,
        mass: 0.9,
      }}
      onClick={onClick}
    >
      {/* Live Aura — only when focused AND show is hot enough.
          Outer + inner halo; pulse speed escalates with heat level. */}
      {focused && heat.hasAura && (
        <div
          className={`absolute inset-0 momentum-aura momentum-aura-${show.heat_level}`}
          style={{
            "--aura-inner": heat.accent,
            "--aura-outer": heat.accent + "55",
          }}
          aria-hidden="true"
        />
      )}

      <Link
        to={`/stream/${show.id}`}
        className={`relative block w-[260px] sm:w-[300px] rounded-3xl overflow-hidden ring-1 ring-white/10 ${
          focused
            ? "shadow-[0_30px_80px_-20px_rgba(0,0,0,0.9)]"
            : "shadow-[0_10px_30px_-10px_rgba(0,0,0,0.7)]"
        }`}
        style={{
          background: bg,
          filter: focused ? "none" : "saturate(0.6) brightness(0.7)",
          transition: "filter 600ms ease",
        }}
      >
        <div className="aspect-square w-full relative">
          {/* Spotlight sweep — only on the focused card. */}
          {focused && (
            <div className="absolute inset-0 momentum-spotlight pointer-events-none" />
          )}

          {/* Subtle vignette so type stays legible regardless of bg */}
          <div className="absolute inset-0 bg-gradient-to-b from-transparent via-transparent to-black/70 pointer-events-none" />

          {/* Top-left: LIVE pill with viewer count-up */}
          <div className="absolute top-3 left-3 flex items-center gap-1.5">
            <span className="inline-flex items-center gap-1 bg-red-600 text-white px-2 py-0.5 rounded-full text-[10px] font-black uppercase tracking-widest">
              <span className="w-1.5 h-1.5 rounded-full bg-white animate-pulse" />
              Live
            </span>
            <span className="inline-flex items-center gap-1 bg-black/40 backdrop-blur-md text-white px-2 py-0.5 rounded-full text-[10px] font-bold">
              <UsersThreeIcon size={10} weight="fill" />
              <span data-testid={focused ? "momentum-viewer-count" : undefined}>
                {focused ? viewers : show.viewer_count || 0}
              </span>
            </span>
          </div>

          {/* Top-right: heat badge */}
          {heat.label && (
            <div className="absolute top-3 right-3">
              <span
                className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-black uppercase tracking-widest"
                style={{
                  background: heat.accent,
                  color: "#1a1a1a",
                  boxShadow: focused ? `0 0 16px ${heat.accent}99` : "none",
                }}
              >
                {show.heat_level === "frenzy" ? <FireIcon size={10} weight="fill" /> : <LightningIcon size={10} weight="fill" />}
                {heat.label}
              </span>
            </div>
          )}

          {/* Bottom: title + seller */}
          <div className="absolute bottom-0 left-0 right-0 p-4">
            <div className="flex items-center gap-2 mb-2">
              <SellerAvatar
                picture={show.seller_picture}
                name={show.seller_name}
                size={28}
                className="ring-2 ring-white/40"
              />
              <span className="text-xs font-bold text-white/90 truncate">
                @{(show.seller_name || "seller").toLowerCase().replace(/\s+/g, "_")}
              </span>
            </div>
            <h3
              className="font-display font-black text-base sm:text-lg leading-tight line-clamp-2"
              style={{ textShadow: "0 2px 8px rgba(0,0,0,0.6)" }}
            >
              {show.title || "Live show"}
            </h3>
            <p className="text-[10px] uppercase tracking-widest text-white/60 mt-1.5">
              {show.category}
            </p>
          </div>
        </div>

        {/* Gold metallic edge — top arc + bottom arc rendered with
            inset gradients. Comes alive on the focused card. */}
        <span
          className="absolute inset-0 rounded-3xl pointer-events-none"
          style={{
            border: focused ? "2px solid transparent" : "1px solid transparent",
            background:
              focused
                ? "linear-gradient(135deg,rgba(252,211,77,0.6) 0%,rgba(255,255,255,0.15) 30%,rgba(252,211,77,0) 60%,rgba(252,211,77,0.4) 100%) border-box"
                : "linear-gradient(135deg,rgba(255,255,255,0.15) 0%,transparent 60%) border-box",
            WebkitMask:
              "linear-gradient(#fff 0 0) padding-box, linear-gradient(#fff 0 0)",
            WebkitMaskComposite: "xor",
            maskComposite: "exclude",
          }}
        />
      </Link>

      {/* Mirror reflection underneath — flipped clone of the card art
          fading to transparent. Pure CSS, no DOM duplication needed
          for layout. Only renders when focused (cheap when offscreen). */}
      {focused && (
        <div
          aria-hidden="true"
          className="momentum-reflection"
          style={{ background: bg }}
        />
      )}
    </motion.div>
  );
}
